import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link, useLocation } from "react-router-dom";
import { RefreshCw, Trash2, Inbox, LogOut, Lock } from "lucide-react";
import { Logo } from "@/components/Logo";

const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: true,
});

const STATUSES = ["New", "Contacted", "Scheduled", "Completed"];
const REFERRAL_STATUSES = ["New", "Contacted", "Sold", "Rewarded"];
const STATES = ["New Jersey", "Delaware", "Pennsylvania"];
const SERVICES = [
  "Seamless Gutters",
  "Gutter Guards",
  "Downspout Extensions",
  "Basement Waterproofing",
  "Full System Shield",
];

const statusColor = {
  New: "bg-orange-500/15 text-orange-400 border-orange-500/40",
  Contacted: "bg-sky-500/15 text-sky-400 border-sky-500/40",
  Scheduled: "bg-amber-500/15 text-amber-400 border-amber-500/40",
  Completed: "bg-emerald-500/15 text-emerald-400 border-emerald-500/40",
  Sold: "bg-emerald-500/15 text-emerald-400 border-emerald-500/40",
  Rewarded: "bg-fuchsia-500/15 text-fuchsia-400 border-fuchsia-500/40",
};

const selectCls =
  "rounded-lg border border-white/15 bg-ink px-3 py-2 text-sm text-slate-200 focus:border-orange-500 focus:outline-none";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const GoogleMark = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
    <path fill="#4285F4" d="M23.5 12.3c0-.9-.1-1.5-.3-2.2H12v4.1h6.5c-.1 1.1-.8 2.7-2.4 3.8l3.7 2.9c2.2-2.1 3.7-5.1 3.7-8.6z" />
    <path fill="#34A853" d="M12 24c3.2 0 5.9-1.1 7.9-2.9l-3.7-2.9c-1 .7-2.4 1.2-4.2 1.2-3.2 0-5.9-2.1-6.8-5.1L1.3 17.2C3.3 21.2 7.3 24 12 24z" />
    <path fill="#FBBC05" d="M5.2 14.3c-.2-.7-.4-1.5-.4-2.3s.1-1.6.4-2.3L1.3 6.8C.5 8.4 0 10.1 0 12s.5 3.6 1.3 5.2l3.9-2.9z" />
    <path fill="#EA4335" d="M12 4.7c1.8 0 3 .8 3.7 1.4l3.3-3.2C17.9 1.1 15.2 0 12 0 7.3 0 3.3 2.8 1.3 6.8l3.9 2.9c.9-3 3.6-5 6.8-5z" />
  </svg>
);

const LoginForm = ({ onLogin, authError }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(authError || "");
  const [busy, setBusy] = useState(false);

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      onLogin(data);
      toast.success("Welcome back.");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-[70vh] items-center justify-center px-4" data-testid="admin-login-screen">
      <form
        onSubmit={submit}
        className="w-full max-w-sm rounded-2xl border border-white/10 bg-ink2 p-8"
        data-testid="admin-login-form"
      >
        <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-orange-500/10">
          <Lock className="h-6 w-6 text-orange-500" />
        </span>
        <h1 className="mt-5 font-display text-3xl font-bold uppercase text-white">Owner Login</h1>
        <p className="mt-2 text-sm text-slate-500">Lead Desk access is restricted to D&amp;G staff.</p>
        <button
          type="button"
          onClick={googleLogin}
          className="mt-6 flex w-full items-center justify-center gap-3 rounded-full border border-white/20 bg-white/5 px-6 py-3 text-sm font-bold text-slate-100 transition-colors hover:border-orange-500/50 hover:bg-white/10"
          data-testid="admin-google-login-button"
        >
          <GoogleMark /> Sign in with Google
        </button>
        <div className="my-5 flex items-center gap-3 text-[10px] font-bold uppercase tracking-[0.25em] text-slate-600">
          <span className="h-px flex-1 bg-white/10" /> or with email <span className="h-px flex-1 bg-white/10" />
        </div>
        <label htmlFor="login-email" className="block text-xs font-bold uppercase tracking-wider text-slate-400">
          Email
        </label>
        <input
          id="login-email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="mt-2 w-full rounded-xl border border-white/15 bg-ink px-4 py-3 text-sm text-slate-100 focus:border-orange-500 focus:outline-none"
          data-testid="admin-login-email"
        />
        <label htmlFor="login-password" className="mt-4 block text-xs font-bold uppercase tracking-wider text-slate-400">
          Password
        </label>
        <input
          id="login-password"
          type="password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="mt-2 w-full rounded-xl border border-white/15 bg-ink px-4 py-3 text-sm text-slate-100 focus:border-orange-500 focus:outline-none"
          data-testid="admin-login-password"
        />
        {error && (
          <p className="mt-3 text-sm text-red-400" data-testid="admin-login-error">
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={busy}
          className="mt-6 w-full rounded-full bg-orange-500 px-6 py-3 text-sm font-bold uppercase tracking-wide text-ink transition-colors hover:bg-orange-600 hover:text-white disabled:opacity-60"
          data-testid="admin-login-submit"
        >
          {busy ? "Signing in…" : "Sign In"}
        </button>
      </form>
    </div>
  );
};

export default function Admin() {
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [tab, setTab] = useState("quotes");
  const [quotes, setQuotes] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [state, setState] = useState("");
  const [service, setService] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (location.state?.user) {
      toast.success("Welcome back.");
      return;
    }
    api
      .get("/auth/me")
      .then(({ data }) => setUser(data))
      .catch(() => setUser(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (state) params.state = state;
      if (service) params.service = service;
      const [{ data: q }, { data: r }] = await Promise.all([
        api.get("/quotes", { params }),
        api.get("/referrals"),
      ]);
      setQuotes(q);
      setReferrals(r);
    } catch {
      toast.error("Could not load Lead Desk data.");
    } finally {
      setLoading(false);
    }
  }, [state, service]);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  const setQuoteStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/quotes/${id}/status`, { status });
      setQuotes((q) => q.map((item) => (item.id === id ? data : item)));
      toast.success(`Marked as ${status}`);
    } catch {
      toast.error("Status update failed.");
    }
  };

  const setReferralStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/referrals/${id}/status`, { status });
      setReferrals((r) => r.map((item) => (item.id === id ? data : item)));
      toast.success(`Marked as ${status}`);
    } catch {
      toast.error("Status update failed.");
    }
  };

  const removeQuote = async (id) => {
    try {
      await api.delete(`/quotes/${id}`);
      setQuotes((q) => q.filter((item) => item.id !== id));
      toast.success("Enquiry deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  const removeReferral = async (id) => {
    try {
      await api.delete(`/referrals/${id}`);
      setReferrals((r) => r.filter((item) => item.id !== id));
      toast.success("Referral deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(false);
  };

  const tabCls = (t) =>
    `rounded-full px-5 py-2 text-sm font-bold uppercase tracking-wide transition-colors ${
      tab === t
        ? "bg-orange-500 text-ink"
        : "border border-white/15 text-slate-300 hover:border-orange-500/50 hover:text-orange-500"
    }`;

  return (
    <div className="min-h-screen bg-ink text-slate-100">
      <header className="border-b border-white/10 bg-ink2/60 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-8">
          <Link to="/" className="flex items-center gap-3" data-testid="admin-home-link">
            <Logo className="h-9 w-9" />
            <span className="font-display text-lg font-bold uppercase tracking-wide">
              Lead Desk <span className="text-orange-500">/ D&amp;G</span>
            </span>
          </Link>
          {user && (
            <div className="flex items-center gap-3">
              <button
                onClick={load}
                className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm font-semibold text-slate-300 transition-colors hover:border-orange-500/50 hover:text-orange-500"
                data-testid="admin-refresh-button"
              >
                <RefreshCw className="h-4 w-4" /> Refresh
              </button>
              <button
                onClick={logout}
                className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm font-semibold text-slate-300 transition-colors hover:border-red-400/50 hover:text-red-400"
                data-testid="admin-logout-button"
              >
                <LogOut className="h-4 w-4" /> Logout
              </button>
            </div>
          )}
        </div>
      </header>

      {user === null ? (
        <div className="py-24 text-center text-sm text-slate-500" data-testid="admin-auth-loading">
          Checking access…
        </div>
      ) : !user ? (
        <LoginForm onLogin={setUser} authError={location.state?.authError} />
      ) : (
        <main className="mx-auto max-w-7xl px-4 py-10 sm:px-8">
          <div className="flex flex-wrap items-center gap-3" data-testid="admin-tabs">
            <button className={tabCls("quotes")} onClick={() => setTab("quotes")} data-testid="admin-tab-quotes">
              Enquiries ({quotes.length})
            </button>
            <button className={tabCls("referrals")} onClick={() => setTab("referrals")} data-testid="admin-tab-referrals">
              Referrals ({referrals.length})
            </button>
          </div>

          {tab === "quotes" && (
            <>
              <div className="mt-6 flex flex-wrap items-center gap-3" data-testid="admin-filters">
                <select value={state} onChange={(e) => setState(e.target.value)} className={selectCls} data-testid="admin-filter-state">
                  <option value="">All States</option>
                  {STATES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
                <select value={service} onChange={(e) => setService(e.target.value)} className={selectCls} data-testid="admin-filter-service">
                  <option value="">All Services</option>
                  {SERVICES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
                <span className="text-sm text-slate-500" data-testid="admin-quote-count">
                  {quotes.length} enquir{quotes.length === 1 ? "y" : "ies"}
                </span>
              </div>

              <div className="mt-6 overflow-x-auto rounded-2xl border border-white/10" data-testid="admin-quote-table">
                <table className="w-full min-w-[860px] text-left text-sm">
                  <thead className="bg-ink2 text-xs uppercase tracking-wider text-slate-500">
                    <tr>
                      <th className="px-5 py-4">Customer</th>
                      <th className="px-5 py-4">Contact</th>
                      <th className="px-5 py-4">State</th>
                      <th className="px-5 py-4">Service</th>
                      <th className="px-5 py-4">Notes</th>
                      <th className="px-5 py-4">Received</th>
                      <th className="px-5 py-4">Status</th>
                      <th className="px-5 py-4" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {quotes.map((q) => (
                      <tr key={q.id} className="bg-ink transition-colors hover:bg-ink2/60" data-testid={`admin-row-${q.id}`}>
                        <td className="px-5 py-4 font-semibold text-slate-100">{q.full_name}</td>
                        <td className="px-5 py-4 text-slate-400">
                          <div>{q.phone}</div>
                          <div className="text-xs text-slate-500">{q.email}</div>
                        </td>
                        <td className="px-5 py-4 text-slate-300">{q.state}</td>
                        <td className="px-5 py-4 text-slate-300">{q.service}</td>
                        <td className="max-w-[220px] truncate px-5 py-4 text-slate-500" title={q.notes}>
                          {q.notes || "—"}
                        </td>
                        <td className="px-5 py-4 text-slate-500">
                          {new Date(q.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-5 py-4">
                          <select
                            value={q.status}
                            onChange={(e) => setQuoteStatus(q.id, e.target.value)}
                            className={`rounded-full border px-3 py-1.5 text-xs font-bold ${statusColor[q.status]} bg-ink focus:outline-none`}
                            data-testid={`admin-status-${q.id}`}
                          >
                            {STATUSES.map((s) => (
                              <option key={s} value={s}>{s}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-5 py-4">
                          <button
                            onClick={() => removeQuote(q.id)}
                            className="text-slate-600 transition-colors hover:text-red-400"
                            aria-label="Delete enquiry"
                            data-testid={`admin-delete-${q.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {!loading && quotes.length === 0 && (
                  <div className="flex flex-col items-center gap-3 py-16 text-slate-500" data-testid="admin-empty-state">
                    <Inbox className="h-8 w-8" />
                    <p className="text-sm">No enquiries yet — new quote requests land here.</p>
                  </div>
                )}
                {loading && (
                  <div className="py-16 text-center text-sm text-slate-500" data-testid="admin-loading">
                    Loading…
                  </div>
                )}
              </div>
            </>
          )}

          {tab === "referrals" && (
            <div className="mt-6 overflow-x-auto rounded-2xl border border-white/10" data-testid="admin-referral-table">
              <table className="w-full min-w-[860px] text-left text-sm">
                <thead className="bg-ink2 text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="px-5 py-4">Referred By</th>
                    <th className="px-5 py-4">Their Phone</th>
                    <th className="px-5 py-4">Friend</th>
                    <th className="px-5 py-4">Friend's Phone</th>
                    <th className="px-5 py-4">Notes</th>
                    <th className="px-5 py-4">Received</th>
                    <th className="px-5 py-4">Status</th>
                    <th className="px-5 py-4" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {referrals.map((r) => (
                    <tr key={r.id} className="bg-ink transition-colors hover:bg-ink2/60" data-testid={`admin-referral-row-${r.id}`}>
                      <td className="px-5 py-4 font-semibold text-slate-100">{r.referrer_name}</td>
                      <td className="px-5 py-4 text-slate-400">{r.referrer_phone}</td>
                      <td className="px-5 py-4 font-semibold text-slate-100">{r.referee_name}</td>
                      <td className="px-5 py-4 text-slate-400">{r.referee_phone}</td>
                      <td className="max-w-[220px] truncate px-5 py-4 text-slate-500" title={r.notes}>
                        {r.notes || "—"}
                      </td>
                      <td className="px-5 py-4 text-slate-500">
                        {new Date(r.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-5 py-4">
                        <select
                          value={r.status}
                          onChange={(e) => setReferralStatus(r.id, e.target.value)}
                          className={`rounded-full border px-3 py-1.5 text-xs font-bold ${statusColor[r.status]} bg-ink focus:outline-none`}
                          data-testid={`admin-referral-status-${r.id}`}
                        >
                          {REFERRAL_STATUSES.map((s) => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-5 py-4">
                        <button
                          onClick={() => removeReferral(r.id)}
                          className="text-slate-600 transition-colors hover:text-red-400"
                          aria-label="Delete referral"
                          data-testid={`admin-referral-delete-${r.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!loading && referrals.length === 0 && (
                <div className="flex flex-col items-center gap-3 py-16 text-slate-500" data-testid="admin-referral-empty">
                  <Inbox className="h-8 w-8" />
                  <p className="text-sm">No referrals yet — when customers refer friends, they land here.</p>
                </div>
              )}
              {loading && (
                <div className="py-16 text-center text-sm text-slate-500">Loading…</div>
              )}
            </div>
          )}
        </main>
      )}
    </div>
  );
}
